import { Box, Button, LinearProgress, Tab, Tabs } from '@mui/material';
import { withStyles } from '@mui/styles';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';

import stockVideo from '@/assets/images/stock-video.svg';
import Extras from '@/components/Extras/Extras';
import VNAccessSettings from '@/components/VNAccessSettings/VNAccessSettings';
import DisplaySettingsForm from '@/components/VNAssetEditor/DisplaySettingsForm';
import Preview from '@/components/VNAssetEditor/Preview';
import VideoForm from '@/components/VNAssetUpload/forms/video';
import VNPortlet from '@/components/VNPortlet/VNPortlet';
import { useProgress } from '@/contexts/ProgressProvider';
import { useTitle } from '@/contexts/TitleContext';
import { FuelPortalService } from '@/services';
import { AssetForm } from '@/store/forms';
import { applicationDataSelector } from '@/store/slices/application-data';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import { SaveAssetJobsSelector, startJob } from '@/store/slices/saveAssetJobsState/SaveAssetJobsState';
import { toSaveAssetRequest } from '@/store/slices/saveAssetJobsState/toSaveAssetRequest';
import useAppUtils from '@/utils/appUtils';
import { cliptoolUtils } from '@/utils/cliptoolUtils';

import { enhanceFetchedAsset } from '../assets/asset-listing/enhanceFetchedAsset';

const StyledLinearProgress = withStyles({
  colorPrimary: {
    backgroundColor: 'lightgray',
  },
  barColorPrimary: {
    backgroundColor: '#512da8',
  },
})(LinearProgress);

const MoviesEdit = () => {
  const { jobs, ingestInProgress } = useSelector(SaveAssetJobsSelector);
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, setProgress } = useProgress();
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [tab, setTab] = useState<string>('details');
  const { ouData, categories } = useSelector(applicationDataSelector);
  const { permissions, fuelUserAccessData } = useSelector(fuelUserAccessSliceSelector);
  const flags = useFlags();
  const { alert } = useAppUtils();
  const { setTitle } = useTitle();

  useEffect(() => {
    const processJob = async () => {
      const originalAsset = await FuelPortalService.getClipById(id);
      const enhancedAsset = enhanceFetchedAsset(originalAsset, permissions, flags, false, [], [], ouData);

      const assetForm = await AssetForm.createFromData(enhancedAsset, permissions, null);

      // set the important values for Preview
      setValue('originalAssetData', assetForm.originalAssetData);
      setValue('assetSrc', assetForm.assetSrc);
      setValue('schemaType', assetForm.schemaType);
      setValue('markersForPlayer', assetForm.markersForPlayer);
    };

    if (!ingestInProgress) {
      processJob();
    }
  }, [ingestInProgress]);

  const methods = useForm({
    defaultValues: {} as AssetForm,
  });
  const { handleSubmit, reset, getValues, setValue, watch } = methods;
  const assetSrc = watch('assetSrc');

  const initData = async () => {
    setProgress(true, 'Loading Movie...');

    try {
      const originalAsset = await FuelPortalService.getClipById(id);
      const enhancedAsset = enhanceFetchedAsset(originalAsset, permissions, flags, false, [], [], ouData);

      const assetForm = await AssetForm.createFromData(enhancedAsset, permissions, null);
      reset(assetForm);
      setIsInitialized(true);
    } catch (error) {
      alert('Unable to fetch data due to unexpected error', 'error');
      console.error('Fetch Error', error);
    } finally {
      setProgress(false);
    }
  };

  const onUploadThumbnail = async (file: File) => {
    const allowedFiles = ['.JPEG', '.JPG', '.PNG', '.GIF'];
    const regex = new RegExp(`([\\W\\S])+(${allowedFiles.join('|')})$`, 'i');
    const message = allowedFiles.join(', ');
    const bodyText = `Please upload files having extensions: ${message} only.`;
    if (file && !regex.test(file && file.name.toLowerCase())) {
      alert(bodyText, 'error');
      return;
    }

    const imgBlob = await cliptoolUtils.getFileImageBlob(file);

    const assetForm = getValues();
    const frozenForm = cloneDeep(assetForm);
    frozenForm.ingestFile = file;
    // console.log('onUploadThumbnail', { frozenForm });
    await dispatch(startJob({ form: frozenForm, jobType: 'Thumbnail' }));

    // update the form
    reset(frozenForm);

    // optimistic update
    setValue('originalAssetData.data.thumbnail.tempThumbnailBlob', imgBlob);
  };

  const onSave = async (form: AssetForm) => {
    const payload = toSaveAssetRequest(form, ouData.schemas, categories, fuelUserAccessData, ouData);
    setProgress(true, 'Saving movie...');
    try {
      // for now move the categories from common to the payload root
      const updatedCategories = payload.data.common.categories.map((cId: string) => {
        return categories.find((c) => c.id === cId);
      });
      payload.categories = updatedCategories.filter((c) => c != null);

      // make sure category is never removed
      const movieCategory = ouData.categories.find((c) => c.name === 'Movies');

      if (movieCategory && payload.categories.findIndex((c) => c.id === movieCategory.id) === -1) {
        payload.categories.push(movieCategory);
      }

      const newAsset = await FuelPortalService.updateAsset(payload);
      if (newAsset) {
        alert('Movie successfully saved.');
        navigate('/movies/list');
      }
    } catch (ex) {
      alert('Error saving movie.');
      console.error('Unable to save movie ', ex);
    } finally {
      setProgress(false);
    }
  };

  useEffect(() => {
    setTitle('Edit Movie');
    if (id) {
      initData();
    }
  }, [id]);

  return (
    <div className="fullHeight movies-edit-container">
      {isInitialized && (
        <FormProvider {...methods}>
          <div className="movies-container ">
            <div className="tab-header-container">
              <Tabs value={tab} onChange={(_, value: string) => setTab(value)} aria-label="live guide tabs">
                <Tab label="Details" value="details" />
                <Tab label="Video Assets" value="assets" />
                <Tab label="Access Schedules" value="accessSchedules" />
              </Tabs>
            </div>
            <div className="tab-body-container vn-asset-editor">
              <VNPortlet>
                {tab === 'details' && (
                  <div className="body expanded">
                    <div className="column left">
                      {assetSrc ? (
                        <Preview videoId="player1" type="video" />
                      ) : (
                        <div className="player-container">
                          <div className="asset-player">
                            <div className="ingesting">
                              <img src={stockVideo} alt="Uploading Asset" />
                              <h3>Uploading Asset...</h3>
                              <StyledLinearProgress
                                variant={jobs?.[0] ? 'determinate' : 'indeterminate'}
                                value={jobs?.[0]?.progress ?? 0}
                                sx={{ height: '8px', borderRadius: '10px' }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      <VideoForm />
                    </div>
                    <div className="column right">
                      <DisplaySettingsForm type="video" uploadThumbnail={onUploadThumbnail} />
                    </div>
                  </div>
                )}
                {tab === 'assets' && (
                  <div>
                    <Extras />
                  </div>
                )}
                {tab === 'accessSchedules' && <VNAccessSettings name="accessSettings" />}
              </VNPortlet>
            </div>
          </div>
          <div className="button-container">
            <Box className="button-section">
              <Box sx={{ flex: '1 1 auto' }} />
              <Button variant="contained" component={Link} to="/movies/list" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{ mr: 1 }}
                color="success"
                onClick={handleSubmit(onSave)}
                disabled={loading || ingestInProgress}
              >
                Save
              </Button>
            </Box>
          </div>
        </FormProvider>
      )}
    </div>
  );
};

export default MoviesEdit;
